import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import { plans } from "@/config/stripePlans";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, MailCheck, MoreHorizontal, Plus, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import {
  StaffInviteDialog,
  StaffRevokeDialog,
  StaffSeatLimitDialog,
  type StaffInviteSubmit,
} from "@/components/admin_dashboard/StaffAccessDialogs";

type StaffRow = {
  id: string;
  email: string;
  display_name: string | null;
  outlet_id: string;
  status: string;
  invited_at: string | null;
  accepted_at: string | null;
  user_id: string | null;
};

/** One person, with every outlet grant they hold in this business. */
type StaffMember = {
  email: string;
  displayName: string | null;
  status: "active" | "pending" | "revoked";
  liveOutletIds: string[];
  liveRowIds: string[];
  invitedAt: string | null;
};

const STATUS_STYLES: Record<StaffMember["status"], string> = {
  active: "bg-green-100 text-green-800",
  pending: "bg-amber-100 text-amber-800",
  revoked: "bg-muted text-muted-foreground",
};

const STATUS_LABELS: Record<StaffMember["status"], string> = {
  active: "Active",
  pending: "Invited",
  revoked: "Revoked",
};

const LIVE_STATUSES = ["pending", "active"];

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

type Props = {
  onNavigateToSection?: (section: string, extraParams?: Record<string, string>) => void;
};

/**
 * Owner-facing staff roster: invite people by email, scope each of them to
 * specific outlets, and revoke access. Seats are counted per person (not per
 * outlet grant) against the plan's staff allowance.
 */
const StaffAccessCard = ({ onNavigateToSection }: Props) => {
  const { businessId, subscriptionTier } = useAuth();
  const { outlets } = useOutlet();

  const [rows, setRows] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [limitOpen, setLimitOpen] = useState(false);
  const [revoking, setRevoking] = useState<StaffMember | null>(null);

  const plan = plans[(subscriptionTier || "free").toLowerCase()] || plans.free;
  const seatLimit = plan.limits.staff;

  const load = useCallback(async () => {
    if (!businessId) return;
    const { data, error } = await supabase
      .from("staff_members")
      .select("id, email, display_name, outlet_id, status, invited_at, accepted_at, user_id")
      .eq("business_id", businessId)
      .order("invited_at", { ascending: false });

    if (error) {
      toast.error("Could not load your staff list.");
    } else {
      setRows((data || []) as StaffRow[]);
    }
    setLoading(false);
  }, [businessId]);

  useEffect(() => {
    load();
  }, [load]);

  const members = useMemo<StaffMember[]>(() => {
    const byEmail = new Map<string, StaffRow[]>();
    for (const row of rows) {
      const key = row.email.toLowerCase();
      byEmail.set(key, [...(byEmail.get(key) || []), row]);
    }

    return Array.from(byEmail.entries())
      .map(([email, memberRows]) => {
        const live = memberRows.filter((r) => LIVE_STATUSES.includes(r.status));
        const status: StaffMember["status"] = live.some((r) => r.status === "active")
          ? "active"
          : live.length > 0
            ? "pending"
            : "revoked";

        return {
          email,
          displayName: memberRows.find((r) => r.display_name)?.display_name ?? null,
          status,
          liveOutletIds: live.map((r) => r.outlet_id),
          liveRowIds: live.map((r) => r.id),
          invitedAt: memberRows[0]?.invited_at ?? null,
        };
      })
      .sort((a, b) => {
        const order = { active: 0, pending: 1, revoked: 2 };
        return order[a.status] - order[b.status] || a.email.localeCompare(b.email);
      });
  }, [rows]);

  const seatsUsed = members.filter((m) => m.status !== "revoked").length;
  const atLimit = seatLimit !== null && seatsUsed >= seatLimit;

  const outletName = useCallback(
    (outletId: string) =>
      outlets.find((o) => o.outlet_id === outletId)?.outlet_name ?? "Removed outlet",
    [outlets]
  );

  const openInvite = () => {
    // A person who already holds a seat isn't taking a new one, so editing stays
    // available at the cap. Only brand-new invitations are blocked.
    if (atLimit) {
      setLimitOpen(true);
      return;
    }
    setEditing(null);
    setInviteOpen(true);
  };

  const sendInvite = async ({ email, displayName, outletIds }: StaffInviteSubmit) => {
    if (!businessId) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("invite-staff", {
        body: {
          email,
          displayName,
          businessId,
          outletIds,
          origin: window.location.origin,
        },
      });

      if (error || data?.success === false) {
        if (data?.code === "STAFF_LIMIT_REACHED") {
          setInviteOpen(false);
          setLimitOpen(true);
          return;
        }
        if (data?.code === "ALREADY_HAS_ACCESS") {
          // Nothing went wrong. The owner just re-picked outlets this person
          // already works in, so no invitation was sent.
          toast.info(data.error);
          return;
        }
        toast.error(data?.error || "Could not send the invitation. Please try again.");
        return;
      }

      toast.success(data?.message || `An invitation has been sent to ${email}.`);
      setInviteOpen(false);
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  const saveOutlets = async ({ email, displayName, outletIds }: StaffInviteSubmit) => {
    if (!businessId || !editing) return;
    setSubmitting(true);
    try {
      const removed = editing.liveOutletIds.filter((id) => !outletIds.includes(id));
      const added = outletIds.filter((id) => !editing.liveOutletIds.includes(id));

      // Removals go through the edge function so the staff member is told their
      // access changed, rather than finding out by hitting a wall.
      if (removed.length > 0) {
        const { data, error } = await supabase.functions.invoke("manage-staff-access", {
          body: {
            action: "remove-outlets",
            email,
            businessId,
            outletIds: removed,
            origin: window.location.origin,
          },
        });

        if (error || data?.success === false) {
          toast.error(data?.error || "Could not update outlet access. Please try again.");
          return;
        }
      }

      // Added outlets go through the invite function: it re-checks the seat
      // limit, and grants immediately (no second email round-trip) when this
      // person has already accepted.
      if (added.length > 0) {
        const { data, error } = await supabase.functions.invoke("invite-staff", {
          body: {
            email,
            displayName,
            businessId,
            outletIds: added,
            origin: window.location.origin,
          },
        });
        if (error || data?.success === false) {
          toast.error(data?.error || "Could not grant the new outlets. Please try again.");
          return;
        }
      }

      if (removed.length === 0 && added.length === 0) {
        setInviteOpen(false);
        return;
      }

      toast.success(`Updated outlet access for ${email}.`);
      setInviteOpen(false);
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRevoke = async () => {
    if (!businessId || !revoking) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-staff-access", {
        body: {
          action: "revoke",
          email: revoking.email,
          businessId,
          origin: window.location.origin,
        },
      });

      if (error || data?.success === false) {
        toast.error(data?.error || "Could not revoke access. Please try again.");
        return;
      }
      toast.success(data?.message || `Access revoked for ${revoking.email}.`);
      setRevoking(null);
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  const resendInvite = async (member: StaffMember) => {
    if (!businessId) return;
    setBusyEmail(member.email);
    try {
      const { data, error } = await supabase.functions.invoke("invite-staff", {
        body: {
          email: member.email,
          displayName: member.displayName || "",
          businessId,
          outletIds: member.liveOutletIds,
          origin: window.location.origin,
        },
      });
      if (error || data?.success === false) {
        toast.error(data?.error || "Could not resend the invitation.");
        return;
      }
      toast.success(`Invitation resent to ${member.email}.`);
      await load();
    } finally {
      setBusyEmail(null);
    }
  };

  const seatSummary =
    seatLimit === null
      ? `${seatsUsed} in use · unlimited on ${plan.name}`
      : `${seatsUsed} of ${seatLimit} ${seatLimit === 1 ? "seat" : "seats"} used on ${plan.name}`;

  return (
    <div className="space-y-6">
      <div className="module-card">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-base font-heading font-semibold text-foreground">Staff accounts</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Invite your team into Dibs and choose which outlets each person can work in.
              Billing &amp; plan, connectors, and this page stay owner-only.
            </p>
            <p className="mt-2 text-sm font-medium text-foreground">{seatSummary}</p>
          </div>
          <Button size="sm" className="standard-action-button shrink-0" onClick={openInvite}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite staff
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-border p-8 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </span>
            <h3 className="mt-4 text-base font-semibold text-foreground">No staff yet</h3>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              Invite a colleague by email. They'll set their own password, then see the
              customers, bookings, and workflows for the outlets you choose.
            </p>
            <Button size="sm" className="standard-action-button mt-4" onClick={openInvite}>
              <Plus className="mr-2 h-4 w-4" />
              Invite your first staff member
            </Button>
          </div>
        ) : (
          <ul className="space-y-3">
            {members.map((member) => (
              <li
                key={member.email}
                className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-border p-4"
              >
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {member.displayName || member.email}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[member.status]}`}
                    >
                      {STATUS_LABELS[member.status]}
                    </span>
                  </div>
                  {member.displayName && (
                    <p className="truncate text-sm text-muted-foreground">{member.email}</p>
                  )}
                  {member.liveOutletIds.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {member.liveOutletIds.map((outletId) => (
                        <Badge key={outletId} variant="outline" className="font-normal">
                          {outletName(outletId)}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No outlet access</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {member.status === "pending"
                      ? `Invited ${fmt(member.invitedAt)} · waiting for them to accept`
                      : member.status === "active"
                        ? `Invited ${fmt(member.invitedAt)}`
                        : "Access revoked"}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {member.status === "pending" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="neutral-action-button standard-action-button"
                      disabled={busyEmail === member.email}
                      onClick={() => resendInvite(member)}
                    >
                      {busyEmail === member.email ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <MailCheck className="mr-2 h-4 w-4" />
                      )}
                      Resend
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="neutral-action-button h-9 w-9"
                        aria-label={`Manage ${member.email}`}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => {
                          if (member.status === "revoked" && atLimit) {
                            setLimitOpen(true);
                            return;
                          }
                          setEditing(member);
                          setInviteOpen(true);
                        }}
                      >
                        {member.status === "revoked" ? "Restore access" : "Manage outlet access"}
                      </DropdownMenuItem>
                      {member.status !== "revoked" && (
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => setRevoking(member)}
                        >
                          Revoke access
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <StaffInviteDialog
        open={inviteOpen}
        onOpenChange={(open) => {
          setInviteOpen(open);
          if (!open) setEditing(null);
        }}
        outlets={outlets}
        editing={
          editing
            ? {
                email: editing.email,
                displayName: editing.displayName,
                outletIds: editing.liveOutletIds,
              }
            : null
        }
        submitting={submitting}
        onSubmit={editing ? saveOutlets : sendInvite}
      />

      <StaffSeatLimitDialog
        open={limitOpen}
        onOpenChange={setLimitOpen}
        planName={plan.name}
        limit={seatLimit ?? 0}
        onUpgrade={() => {
          setLimitOpen(false);
          onNavigateToSection?.("settings", { settings: "billing" });
        }}
      />

      <StaffRevokeDialog
        open={!!revoking}
        onOpenChange={(open) => !open && setRevoking(null)}
        staffLabel={revoking?.displayName || revoking?.email || "This staff member"}
        outletName={null}
        submitting={submitting}
        onConfirm={confirmRevoke}
      />
    </div>
  );
};

export default StaffAccessCard;
